import { useRef } from 'react';
import { motion, useScroll, useTransform, useInView } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import './About.css';

// Animation variants for content sections
const fadeInVariants = {
  hidden: { opacity: 0, y: 50 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { 
      duration: 0.8,
      ease: "easeOut"
    }
  }
};

const cardVariants = {
  hidden: { opacity: 0, y: 70 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { 
      duration: 0.8,
      ease: [0.25, 0.1, 0.25, 1.0]
    }
  }
};

const ScrollRevealSection = ({ children, delayFactor = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: false, amount: 0.2 });
  
  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={fadeInVariants}
      transition={{ delay: delayFactor * 0.2 }}
    >
      {children}
    </motion.div>
  );
};

const AnimatedCard = ({ image, title, text, emoji, delay = 0, isReversed = false }) => {
  const cardRef = useRef(null);
  const isInView = useInView(cardRef, { once: false, amount: 0.2 });
  
  return (
    <motion.div
      ref={cardRef}
      className={`about-card ${isReversed ? 'reverse' : ''}`}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={cardVariants}
      transition={{ delay }}
    >
      <div className="card-image-container">
        <div className="card-glassy-frame">
          <img src={image} alt={title} className="card-image" />
        </div>
      </div>
      <div className="card-content">
        <h3 className="card-title">{title}</h3>
        <p className="card-text">{text}</p>
      </div>
    </motion.div>
  );
};

const About = () => {
  const navigate = useNavigate();
  const aboutRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: aboutRef,
    offset: ["start start", "end end"]
  });
  
  // Transform values for parallax effects
  const backgroundY = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);

  // Animation sequence for highlights section
  const highlightVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.2,
        duration: 0.5,
        ease: "easeOut"
      }
    })
  };

  return (
    <section className="about-section" id="about" ref={aboutRef}>
      {/* Parallax Background with golden overlay */}
      <motion.div 
        className="parallax-background"
        style={{ y: backgroundY }}
      />
      <div className="overlay-gradient"></div>

      <div className="about-container">
        {/* Header Section */}
        <ScrollRevealSection>
          <div className="about-header">
            <h2 className="gold-underline">Our Essence</h2>
            <p className="section-subtitle">Quality. Craft. Sustainability.</p>
          </div>
        </ScrollRevealSection>
        
        {/* Intro Section */}
        <ScrollRevealSection delayFactor={1}>
          <div className="about-intro">
            <h3 className="intro-title">
              <span className="animated-text">Bringing Nature's Elegance to Life</span>
            </h3>
            <p className="intro-text">
              At <strong>INFUSE</strong>, we believe in the enduring magic of nature. Our carefully curated collection 
              transforms spaces with elegance and tranquility. Each piece is hand-selected and preserved 
              to maintain its original charm—ensuring beauty that lasts well beyond the season.
            </p>
          </div>
        </ScrollRevealSection>

        {/* Card Sections */}
        <div className="about-cards-container">
          <AnimatedCard 
            image="/images/side-image-1.jpg"
            title="Art of Dry Botanicals 🌿"
            text="Our exquisite botanicals — including stunning floral arrangements and enchanting wreaths — create a serene and captivating ambiance that never goes out of style. Discover how our pieces can transform any space."
            delay={0.2}
          />
          
          <AnimatedCard 
            image="/images/side-image-2.jpg"
            title="Sustainable Elegance ♻️"
            text="Eco-friendly, long-lasting alternatives to fresh flowers. Our dry botanicals offer beauty without the environmental impact - no water consumption, no pesticides, and no daily care required."
            delay={0.4}
            isReversed={true}
          />
          
          <AnimatedCard 
            image="/images/side-image-3.jpg"
            title="Global Craftsmanship 🌎"
            text="We ethically source our botanicals from India, China, Africa & Europe, working with skilled artisans who turn nature's gifts into exquisite decor pieces that bring beauty to your spaces."
            delay={0.6}
          />
        </div>
        
        {/* Highlights Section */}
        <ScrollRevealSection delayFactor={5}>
          <div className="highlights-container">
            <h3 className="highlights-title">Why Choose INFUSE</h3>
            
            <div className="highlights-grid">
              {[
                {
                  icon: "🤝",
                  title: "Ethical Sourcing",
                  text: "From India, China, Africa & Europe"
                },
                {
                  icon: "♻️",
                  title: "Eco-Friendly",
                  text: "Long-lasting alternatives to fresh flowers"
                },
                {
                  icon: "📦",
                  title: "Premium Packaging",
                  text: "Ensuring perfect condition on arrival"
                },
                {
                  icon: "💚",
                  title: "Low-Waste Beauty",
                  text: "No water, no pesticides, no daily care"
                },
                {
                  icon: "🌿",
                  title: "Natural Elements",
                  text: "100% authentic botanical materials"
                },
                {
                  icon: "✨",
                  title: "Artisan Crafted",
                  text: "Handmade with attention to detail"
                }
              ].map((highlight, index) => (
                <motion.div 
                  key={index}
                  className="highlight-box"
                  custom={index}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: false, amount: 0.1 }}
                  variants={highlightVariants}
                  whileHover={{ scale: 1.05, boxShadow: "0 10px 25px rgba(212,175,55,0.3)" }}
                  transition={{ type: "spring", stiffness: 400, damping: 10 }}
                >
                  <div className="highlight-icon">{highlight.icon}</div>
                  <h4>{highlight.title}</h4>
                  <p>{highlight.text}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </ScrollRevealSection>
        
        {/* Company Info Section */}
        <ScrollRevealSection delayFactor={7}>
          <div className="company-info">
            <h3 className="company-title">Nature's Creation by INFUSE</h3>
            <div className="info-content">
              <div className="info-text">
                <p>
                  This is a unique collection of over <strong>200+ nature-inspired creative products</strong>, brought to the GCC 
                  by <strong>Natures Creations by INFUSE Trading LLC</strong> in collaboration with <strong>Bhalotia International</strong>.
                </p>
                <p>
                  Bhalotia International is a globally recognized brand known for its contemporary and innovative dry flower 
                  arrangements. Since 2007, the company has expanded across multiple continents, setting new standards in botanical 
                  artistry.
                </p>
              </div>
              <motion.img 
                src="/images/side-image-4.jpg" 
                alt="INFUSE collaboration with Bhalotia" 
                className="info-image"
                whileHover={{ scale: 1.03, rotate: 1 }}
                transition={{ type: "spring", stiffness: 300, damping: 15 }}
              />
            </div>
          </div>
        </ScrollRevealSection>

        {/* Commitment Banner */}
        <ScrollRevealSection delayFactor={9}>
          <motion.div
            className="commitment-banner"
            whileInView={{ 
              boxShadow: ["0 10px 30px rgba(0,0,0,0.2)", "0 15px 40px rgba(212,175,55,0.3)", "0 10px 30px rgba(0,0,0,0.2)"]
            }}
            transition={{ duration: 3, repeat: Infinity, repeatType: "reverse" }}
          >
            <div className="banner-content">
              <h3>Infuse Your World With Nature's Elegance</h3>
              <p>Ready to transform your space or place a bulk order?</p>
              <div className="banner-cta">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.98 }}
                  className="gold-button"
                  onClick={() => navigate('/contact')}
                >
                  Contact Us
                </motion.button>
                <span className="gold-text">+971-52-7682302</span>
              </div>
            </div>
          </motion.div>
        </ScrollRevealSection>
      </div>
    </section>
  );
};

export default About;